// Servicio para guardar modelos generados en Firebase Storage
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';

interface ModelData {
  imageUrl: string;
  prompt: string;
  products: Array<{
    id: string;
    name: string;
    category: string;
  }>;
  generatedAt: Date;
}

class ModelStorageService {
  /**
   * Guardar modelo generado en Firebase Storage
   */
  async saveGeneratedModel(
    userId: string,
    modelData: ModelData
  ): Promise<{ success: boolean; downloadUrl?: string; error?: string }> {
    try {
      console.log('💾 Guardando modelo en Firebase Storage:', modelData);

      if (!window.auth?.storage) {
        throw new Error('Firebase Storage no está disponible');
      }

      // Verificar si es una URL placeholder - crear imagen simple
      if (modelData.imageUrl.includes('via.placeholder.com') && !modelData.imageUrl.startsWith('data:image/')) {
        console.log('✅ URL placeholder detectada, creando imagen simple...');
        
        // Crear una imagen simple en lugar de hacer fetch
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 1000;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          // Fondo azul
          ctx.fillStyle = '#6366f1';
          ctx.fillRect(0, 0, 800, 1000);
          
          // Texto blanco
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Modelo Rioplatense', 400, 400);
          ctx.fillText(modelData.products[0]?.name || 'Producto', 400, 450);
        }
        
        // Convertir canvas a blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => {
            resolve(blob!);
          }, 'image/png');
        });
        
        // Crear nombre único para el archivo
        const timestamp = Date.now();
        const fileName = `modelo_${timestamp}.png`;
        
        // Usar el mismo patrón que uploadClothingImage
        const { storage, ref, uploadBytes, getDownloadURL } = window.auth;
        const imagePath = `users/${userId}/modelos-generadas/${fileName}`;
        const imageRef = ref(storage, imagePath);
        
        console.log('📁 Creando carpeta modelos-generadas automáticamente...');
        console.log('📁 Ruta completa:', imagePath);

        // Subir archivo usando el patrón correcto
        const snapshot = await uploadBytes(imageRef, blob);
        
        // Obtener URL de descarga
        const downloadUrl = await getDownloadURL(snapshot.ref);

        console.log('✅ Modelo placeholder guardado exitosamente:', downloadUrl);

        return {
          success: true,
          downloadUrl: downloadUrl
        };
      }
      
      // Si es una data URL (imagen real generada), procesarla
      if (modelData.imageUrl.startsWith('data:image/')) {
        console.log('✅ Imagen real generada detectada (data URL), guardando en Storage');
        
        // Verificar que Firebase Storage esté disponible
        if (!window.auth?.storage) {
          console.error('❌ Firebase Storage no está disponible');
          return {
            success: false,
            error: 'Firebase Storage no está disponible. Intenta recargar la página.'
          };
        }
        
        // Verificar autenticación
        console.log('🔐 Verificando autenticación...');
        console.log('🔐 Usuario actual:', window.auth.auth?.currentUser);
        console.log('🔐 UID:', window.auth.auth?.currentUser?.uid);
        
        if (!window.auth.auth?.currentUser) {
          console.error('❌ Usuario no autenticado');
          return {
            success: false,
            error: 'Usuario no autenticado. Inicia sesión para guardar modelos.'
          };
        }
        
        // Convertir data URL a blob
        const response = await fetch(modelData.imageUrl);
        const blob = await response.blob();
        
        // Crear nombre único para el archivo
        const timestamp = Date.now();
        const fileName = `modelo_${timestamp}.png`;
        
        // Usar el mismo patrón que uploadClothingImage
        const { storage, ref, uploadBytes, getDownloadURL } = window.auth;
        const imagePath = `users/${userId}/modelos-generadas/${fileName}`;
        const imageRef = ref(storage, imagePath);
        
        console.log('📁 Creando carpeta modelos-generadas automáticamente...');
        console.log('📁 Ruta completa:', imagePath);

        // Subir archivo usando el patrón correcto
        const snapshot = await uploadBytes(imageRef, blob);
        
        // Obtener URL de descarga
        const downloadUrl = await getDownloadURL(snapshot.ref);

        console.log('✅ Modelo guardado exitosamente:', downloadUrl);

        return {
          success: true,
          downloadUrl: downloadUrl
        };
      }

      // Para otras URLs, hacer fetch normal
      const response = await fetch(modelData.imageUrl);
      const blob = await response.blob();

      // Crear nombre único para el archivo
      const timestamp = Date.now();
      const fileName = `modelo_${timestamp}.jpg`;
      
      // Usar el mismo patrón que uploadClothingImage
      const { storage, ref, uploadBytes, getDownloadURL } = window.auth;
      const imagePath = `users/${userId}/modelos-generadas/${fileName}`;
      const imageRef = ref(storage, imagePath);
      
      console.log('📁 Creando carpeta modelos-generadas automáticamente...');
      console.log('📁 Ruta completa:', imagePath);

      // Subir archivo usando el patrón correcto
      const snapshot = await uploadBytes(imageRef, blob);
      
      // Obtener URL de descarga
      const downloadUrl = await getDownloadURL(snapshot.ref);

      console.log('✅ Modelo guardado exitosamente:', downloadUrl);

      return {
        success: true,
        downloadUrl: downloadUrl
      };

    } catch (error) {
      console.error('❌ Error guardando modelo:', error);
      
      // Manejar errores específicos
      let errorMessage = 'Error al guardar el modelo. Inténtalo de nuevo.';
      
      if (error instanceof Error) {
        if (error.message.includes('storage/unauthorized')) {
          errorMessage = 'No tienes permisos para guardar en Storage. Verifica tu autenticación.';
        } else if (error.message.includes('storage/object-not-found')) {
          errorMessage = 'No se pudo encontrar la carpeta. Intenta crear la carpeta primero.';
        } else if (error.message.includes('storage/quota-exceeded')) {
          errorMessage = 'Se excedió la cuota de almacenamiento.';
        } else if (error.message.includes('storage/invalid-argument')) {
          errorMessage = 'Argumentos inválidos para el almacenamiento.';
        } else {
          errorMessage = `Error específico: ${error.message}`;
        }
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Obtener lista de modelos generados del usuario
   */
  async getGeneratedModels(userId: string): Promise<ModelData[]> {
    try {
      if (!window.auth?.storage) {
        throw new Error('Firebase Storage no está disponible');
      }

      console.log('📋 Obteniendo modelos generados para usuario:', userId);
      
      const { storage, ref, listAll, getDownloadURL } = window.auth;
      const modelsPath = `users/${userId}/modelos-generadas/`;
      const modelsRef = ref(storage, modelsPath);
      
      // Listar todos los archivos en la carpeta
      const result = await listAll(modelsRef);
      console.log('📁 Archivos encontrados:', result.items.length);
      
      const models: ModelData[] = [];
      
      // Procesar cada archivo
      for (const itemRef of result.items) {
        try {
          const downloadUrl = await getDownloadURL(itemRef);
          const fileName = itemRef.name;
          
          // Extraer información del nombre del archivo si es posible
          const timestamp = fileName.replace('modelo_', '').replace('.png', '');
          
          models.push({
            imageUrl: downloadUrl,
            prompt: `Modelo generado el ${new Date(parseInt(timestamp)).toLocaleDateString()}`,
            products: [{
              id: fileName,
              name: fileName,
              category: 'modelo-generada'
            }],
            generatedAt: new Date(parseInt(timestamp))
          });
          
          console.log('✅ Modelo cargado:', fileName);
        } catch (itemError) {
          console.error('❌ Error cargando archivo:', itemRef.name, itemError);
        }
      }
      
      console.log('📋 Total de modelos cargados:', models.length);
      return models;
      
    } catch (error) {
      console.error('❌ Error obteniendo modelos:', error);
      return [];
    }
  }

  /**
   * Eliminar modelo generado
   */
  async deleteGeneratedModel(userId: string, fileName: string): Promise<boolean> {
    try {
      if (!window.auth?.storage) {
        throw new Error('Firebase Storage no está disponible');
      }

      const { storage, ref, deleteObject } = window.auth;
      const imagePath = `users/${userId}/modelos-generadas/${fileName}`;
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      console.log('🗑️ Modelo eliminado exitosamente');
      return true;
    } catch (error) {
      console.error('❌ Error eliminando modelo:', error);
      return false;
    }
  }
}

// Crear instancia singleton
const modelStorageService = new ModelStorageService();

export default modelStorageService;
export type { ModelData };
